/**
 * «Что нового»: список выпусков (§10, M5).
 *
 * Живёт в коде, а не в БД: это текст, который пишется вместе с изменением и
 * должен уезжать на сервер вместе с ним. Сервер про содержимое выпусков не
 * знает — он хранит только отметку «этот человек уже прочитал `release:N`».
 *
 * Правила, которые легко нарушить и потом не заметить:
 *  1. Новый выпуск — новая запись сверху и новый номер. Номер не переиспользуем:
 *     иначе прочитавшие старую версию не увидят новую.
 *  2. Пишем про то, что человек заметит сам. «Ускорили запросы» ему не про что.
 *  3. Ни одной записи «исправлены ошибки»: это не новость, а гигиена.
 */

import { COACH_KEYS_BY_ROLE, useSeenHints } from '@/shared/api/hints'
import type { Role } from '@/shared/api/types'
import type { IconName } from '@/shared/ui/icon-names'

export interface ReleaseNote {
  /** `release:N`. Должен проходить проверку RELEASE_KEY в backend/app/services/hints.py. */
  key: string
  title: string
  /** Дата выпуска, ISO. Показывается человеку в привычном виде. */
  date: string
  items: { icon: IconName; text: string }[]
}

/** Новые выпуски сверху. */
export const RELEASES: ReleaseNote[] = [
  {
    key: 'release:1',
    title: 'Приложение стало теплее',
    date: '2026-09-04',
    items: [
      {
        icon: 'brand-hello',
        text: 'Новый вид: карточки-наклейки, небо в шапке и свои рисунки вместо эмодзи, которые на каждом телефоне выглядели по-разному.',
      },
      {
        icon: 'feedback-idea',
        text: 'Кнопка «Идея или проблема» — пишите прямо из приложения, мы читаем каждое сообщение.',
      },
      {
        icon: 'plan-ack',
        text: 'Короткие подсказки при первом входе: три шага, дальше вы сами.',
      },
    ],
  },
]

export const RELEASE_KEYS: string[] = RELEASES.map((release) => release.key)

/**
 * Непрочитанные выпуски. Пусто для новичка — ему нечего сравнивать.
 *
 * Условие — пройденный тур целиком, а не одна закрытая подсказка. Иначе
 * шторка новостей выскакивает между первым и вторым шагом тура и выбрасывает
 * человека из него: половина подсказок остаётся непоказанной, и вернуться к
 * ним уже нельзя.
 *
 * Вынесено из компонента, потому что оболочка роли обязана знать об этом
 * заранее: два слоя поверх экрана одновременно читаются как поломка.
 */
export function useUnreadReleases(role: Role | undefined): ReleaseNote[] {
  const seen = useSeenHints()
  if (!role) return []
  if (!COACH_KEYS_BY_ROLE[role].every((key) => seen.has(key))) return []
  return RELEASES.filter((release) => !seen.has(release.key))
}
